import React, { useState, useEffect, useRef, useContext, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Calendar } from 'primereact/calendar';
import { FileUpload } from 'primereact/fileupload';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { AuthContext } from '../../../../Header/auth - context';
import { translateCol } from '../../../../custom/columnI18n';

/* Datum se šalje kroz toUtcIso — bez korekcije offseta `toISOString` ume da
   pomeri dan unazad (vidi CLAUDE.md). */
const toUtcIso = (d) => {
    if (!d) return null;
    const x = d instanceof Date ? d : new Date(d);
    const utc = new Date(x.getTime() - x.getTimezoneOffset() * 60000);
    return utc.toISOString().slice(0, 19) + 'Z';
};

/* Fajl na disku (C:/Dokumenta/...) -> URL koji servira backend (/API/files/...) */
const linkDokumenta = (putanja) => {
    if (!putanja) return null;
    const normalizovana = String(putanja).replace(/\\/g, '/');
    const relativna = normalizovana
        .replace(/^([A-Za-z]:)?\/Dokumenta\//, '')
        .replace(/^\/var\/www\/dokumenta\//, '');
    return `${process.env.REACT_APP_BACKEND_URL}/files/${encodeURI(relativna)}`;
};

/* `Fajl` je putanja priloga IZ BAZE (ne novi izabrani fajl) — po njoj dugme
   prikazuje da prilog već postoji, kao u Administracija → Vozila → Unos. */
const praznaForma = { Naziv: '', DatumDo: null, Napomena: '', Fajl: '' };

const FirmaAlarmi = () => {
    const { t } = useTranslation();
    const auth = useContext(AuthContext);
    const company = useSelector(state => state.company);
    const toast = useRef(null);
    const formRef = useRef(null);

    const [podaci, setPodaci] = useState([]);
    const [forma, setForma] = useState(praznaForma);
    const [selectedId, setSelectedId] = useState(null);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [globalFilter, setGlobalFilter] = useState(null);
    const [fajl, setFajl] = useState(null);
    const [salje, setSalje] = useState(false);

    const zaglavlje = useCallback(() => ({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + auth.token,
    }), [auth.token]);

    /* Jedan handler za sva polja — PrimeReact i za Calendar šalje `target.name`
       i `target.value`, pa isti kod pokriva i tekst i datum (kao u Firma → Unos). */
    const handleChange = (e) => {
        const { name, value } = e.target;
        setForma(prev => ({ ...prev, [name]: value }));
    };

    /* Crveni okvir tek posle pokušaja snimanja — inače prazna forma dočeka
       korisnika sva u crvenom. */
    const invalidControl = (vrednost) => {
        if (!formSubmitted) return false;
        if (vrednost instanceof Date) return false;
        return !String(vrednost ?? '').trim();
    };

    const poruka = (severity, summary, detail) =>
        toast.current?.show({ severity, summary, detail, life: 4000 });

    const ucitaj = useCallback(() => {
        fetch(process.env.REACT_APP_BACKEND_URL + '/company/alarmi', {
            method: 'POST', headers: zaglavlje(),
        })
            .then(r => r.json())
            .then(data => setPodaci(Array.isArray(data) ? data : []))
            .catch(() => poruka('error', t('common.greska'), t('firmaAlarm.greskaUcitavanje')));
    }, [zaglavlje, t]);

    useEffect(() => { ucitaj(); }, [ucitaj]);

    /* Klik na isti red poništava izbor i prazni formu — bez `onRowClick` na
       tabeli, jer bi se sa dugmetom okidao dvaput nad istim stanjem. */
    const izaberiRed = (row) => {
        const id = row?.ID;
        setSelectedId(prev => {
            const next = String(prev) === String(id) ? null : id;
            if (next !== null) {
                setForma({
                    Naziv: row.Naziv || '',
                    DatumDo: row.DatumDoRaw ? new Date(row.DatumDoRaw) : null,
                    Napomena: row.Napomena || '',
                    Fajl: row.Fajl || '',
                });
            } else {
                setForma(praznaForma);
                setFormSubmitted(false);
            }
            setFajl(null);
            return next;
        });
    };

    /* Prilog se kači TEK POSLE snimanja: folder na disku nosi id alarma, koji
       kod novog unosa ne postoji dok INSERT ne prođe. */
    const posaljiPrilog = async (id) => {
        if (!fajl || !id) return;
        const telo = new FormData();
        /* REDOSLED JE BITAN: `id` i `entityType` moraju ići PRE fajla.
           Multer obrađuje delove forme onim redom kojim stignu, a u
           `destination` (middleware/upload.js) čita `req.body.id` da bi
           napravio folder — ako fajl stigne prvi, to polje još ne postoji i
           upload pada sa „Nedostaje id u body". Isti redosled ima i FirmaUnos. */
        telo.append('id', String(id));
        telo.append('entityType', 'firmaAlarm');
        telo.append('uploadedFileAlarm', fajl);

        const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/company/alarmi/fajl', {
            method: 'POST', headers: { Authorization: 'Bearer ' + auth.token }, body: telo,
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(data.message || t('firmaAlarm.greskaPrilog'));

        const red = Array.isArray(data) ? data[0] : data;
        return red?.message || t('firmaAlarm.prilogSacuvan');
    };

    const sacuvaj = async () => {
        setFormSubmitted(true);
        if (!forma.Naziv?.trim() || !forma.DatumDo) return;

        setSalje(true);
        try {
            const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/company/alarmi/save', {
                method: 'POST', headers: zaglavlje(),
                body: JSON.stringify({
                    id: selectedId || null,
                    Naziv: forma.Naziv.trim(),
                    DatumDo: toUtcIso(forma.DatumDo),
                    Napomena: forma.Napomena || '',
                }),
            });
            const data = await r.json();
            if (!r.ok) throw new Error(data.message || t('firmaAlarm.greskaSnimanje'));

            const red = Array.isArray(data) ? data[0] : data;
            if (red?.message && !red?.ID) throw new Error(red.message);

            const porukaPriloga = await posaljiPrilog(red?.ID || selectedId);

            /* Kad je uz snimanje išao i prilog, poruka koraka SAVE („Nema izmena
               u podacima") nije tačna — prilog JESTE izmena, samo se upisuje
               korakom posle. Zato se prikazuje poruka onoga ko je stvarno nešto
               promenio. */
            poruka('success', t('firmaAlarm.naslov'),
                porukaPriloga || red?.message || t('firmaAlarm.sacuvan'));
            setForma(praznaForma);
            setSelectedId(null);
            setFormSubmitted(false);
            setFajl(null);
            ucitaj();
        } catch (e) {
            poruka('error', t('common.greska'), e.message);
        } finally {
            setSalje(false);
        }
    };

    const obrisi = () => {
        if (!selectedId) return;
        confirmDialog({
            message: t('firmaAlarm.potvrdaBrisanja'),
            header: t('firmaAlarm.naslov'),
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: t('common.da'),
            rejectLabel: t('common.ne'),
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    const r = await fetch(process.env.REACT_APP_BACKEND_URL + '/company/alarmi/delete', {
                        method: 'POST', headers: zaglavlje(), body: JSON.stringify({ id: selectedId }),
                    });
                    const data = await r.json();
                    if (!r.ok) throw new Error(data?.message || t('firmaAlarm.greskaBrisanje'));

                    poruka('success', t('firmaAlarm.naslov'), (Array.isArray(data) ? data[0]?.message : data?.message) || t('firmaAlarm.obrisan'));
                    setSelectedId(null);
                    setForma(praznaForma);
                    ucitaj();
                } catch (e) {
                    poruka('error', t('common.greska'), e.message);
                }
            },
        });
    };

    const exportExcel = async () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('AlarmiFirme');
        const headers = ['Naziv', 'Važi do', 'Preostalo dana', 'Napomena'];
        const headerRow = worksheet.addRow(headers.map(h => translateCol(t, h)));
        headerRow.font = { bold: true };
        headerRow.eachCell(cell => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEFEFEF' } };
            cell.border = { top: { style: 'medium' }, left: { style: 'medium' }, bottom: { style: 'medium' }, right: { style: 'medium' } };
        });
        (podaci || []).forEach(r => {
            /* Kolone se čitaju po SIROVOM imenu — prevodi se samo prikaz. */
            const row = worksheet.addRow(headers.map(h => r[h] ?? ''));
            row.eachCell(cell => {
                cell.border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
            });
        });
        worksheet.columns.forEach(col => {
            let max = 10;
            col.eachCell(cell => { if (cell.value) max = Math.max(max, cell.value.toString().length); });
            col.width = max + 2;
        });
        const dt = new Date().toLocaleString('sr-RS', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Belgrade' }).replace(/,/g, '');
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), `${t('firmaAlarm.naslov')} - ${company?.companyName || ''} - ${dt}.xlsx`);
    };

    const tableHeader = (
        <div className="export-buttons fakture-stavke-header">
            <div className="fakture-stavke-radnje">
                <Button type="button" icon="pi pi-file-excel" severity="success" rounded onClick={exportExcel} data-pr-tooltip="XLS" />
                {selectedId && <Button type="button" icon="pi pi-trash" severity="danger" rounded onClick={obrisi} data-pr-tooltip={t('common.delete')} />}
            </div>
            <input type="search" placeholder={t('nova.phPretraga')} className="p-inputtext p-component"
                onInput={e => setGlobalFilter(e.target.value)} />
        </div>
    );

    return (
        <div className="col-12">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Forma levo, tabela desno (50/50) — kao i ostali admin ekrani. */}
            <div className="blagajnaDashboard dnevniceDashboard">
                <Card>
                    {/* Širina polja ide kroz style, ne kroz klasu: PrimeFlex nije
                       instaliran, a PrimeReact core CSS (sa `.p-fluid`) nije uključen —
                       tema je prepisana ručno u magacin.css. */}
                    <form autoComplete="off" ref={formRef}>
                        <div className="gridLicni gridAlarmiFirme">
                            <div className="col-12 md:col-6">
                                <label htmlFor="Naziv">{t('firmaAlarm.naziv')}:</label>
                                <InputText
                                    id="Naziv"
                                    name="Naziv"
                                    style={{ width: '100%' }}
                                    value={forma.Naziv}
                                    onChange={handleChange}
                                    required
                                    invalid={invalidControl(forma.Naziv)}
                                    placeholder={t('firmaAlarm.phNaziv')}
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <label htmlFor="DatumDo">{t('firmaAlarm.vaziDo')}:</label>
                                <Calendar
                                    id="DatumDo"
                                    name="DatumDo"
                                    style={{ width: '100%' }}
                                    value={forma.DatumDo}
                                    onChange={handleChange}
                                    required
                                    invalid={invalidControl(forma.DatumDo)}
                                    /* Maska dozvoljava kucanje datuma, ne samo biranje iz
                                       kalendara. Format je BEZ tačke na kraju — sa njom se
                                       maska 99.99.9999 ne poklapa sa ispisanom vrednošću. */
                                    dateFormat="dd.mm.yy"
                                    mask="99.99.9999"
                                    showIcon
                                    showButtonBar
                                />
                            </div>

                            {/* PRILOG (fajl) */}
                            <div className="col-12 md:col-6">
                                <label>{t('firmaAlarm.prilog')}:</label>
                                {!fajl ? (
                                    <FileUpload
                                        name="uploadedFileAlarm"
                                        mode="basic"
                                        customUpload={false}
                                        auto={false}
                                        /* Kad izabrani alarm već ima prilog u bazi, dugme to i
                                           kaže (žuta podloga + „Fajl u bazi!") — isti obrazac
                                           koji koristi Administracija → Vozila → Unos. */
                                        className={forma.Fajl ? 'btnHasDocument' : ''}
                                        chooseLabel={forma.Fajl ? t('firmaAlarm.fajlUBazi') : t('nova.izaberi')}
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        maxFileSize={5000000}
                                        onSelect={(e) => setFajl(e.files?.[0] ?? null)}
                                    />
                                ) : (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <Button label={fajl.name} outlined type="button" />
                                        <Button
                                            icon="pi pi-times"
                                            type="button"
                                            className="p-button-danger p-button-text p-button-rounded"
                                            onClick={() => setFajl(null)}
                                            tooltip={t('firmaAlarm.ukloniFajl')}
                                            tooltipOptions={{ position: 'top' }}
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="col-12 alarmPuno">
                                <label htmlFor="Napomena">{t('firmaAlarm.napomena')}:</label>
                                <InputTextarea
                                    id="Napomena"
                                    name="Napomena"
                                    style={{ width: '100%' }}
                                    value={forma.Napomena}
                                    onChange={handleChange}
                                    rows={3}
                                    autoResize
                                    placeholder={t('firmaAlarm.phNapomena')}
                                />
                            </div>
                        </div>

                        {/* Dugme je centrirano kroz `buttonUnosZap` (flex + justify-content:
                            center, širina 10%) — isti omotač koji koristi Firma → Unos.
                            `minWidth: fit-content` je obavezan: 10% od kartice koja je i
                            sama na pola ekrana ne primi reč „SAČUVAJ". */}
                        <div className="formUnosZap buttonUnosZap">
                            <button type="submit" onClick={sacuvaj} disabled={salje}
                                style={{ minWidth: 'fit-content', marginTop: '2%' }}
                                className="btn btn-success">
                                {selectedId ? t('admin.sacuvaj') : t('admin.unos')}
                            </button>
                        </div>
                    </form>
                </Card>

                <Card>
                    <Tooltip target=".export-buttons>button" position="bottom" />
                    <DataTable
                        value={podaci || []}
                        paginator rows={10}
                        globalFilter={globalFilter}
                        scrollable scrollHeight="50vh"
                        header={tableHeader}
                        emptyMessage={t('firmaAlarm.nemaAlarma')}
                        resizableColumns columnResizeMode="expand"
                        className="turePregledTable stavke-tabela"
                        /* Istekao rok nosi istu crvenu koju alarmi imaju i u Info-u. */
                        rowClassName={r => {
                            if (String(r.ID) === String(selectedId)) return 'selected-row';
                            return Number(r['Preostalo dana']) < 0 ? 'row-invoice-overdue' : '';
                        }}>
                        <Column header={t('nova.izaberi')} style={{ minWidth: '90px' }}
                            body={row => (
                                <Button label={t('nova.izaberi')} icon="pi pi-eye" type="button"
                                    className="p-button-text p-button-sm" onClick={() => izaberiRed(row)} />
                            )} />
                        <Column field="Naziv" header={translateCol(t, 'Naziv')} sortable style={{ minWidth: '200px' }} />
                        <Column field="Važi do" header={translateCol(t, 'Važi do')} sortable style={{ minWidth: '120px' }} />
                        <Column field="Preostalo dana" header={translateCol(t, 'Preostalo dana')} sortable style={{ minWidth: '130px' }} />
                        <Column field="Napomena" header={translateCol(t, 'Napomena')} style={{ minWidth: '220px' }}
                            body={r => r.Napomena || '-'} />
                        <Column header={translateCol(t, 'Prilog')} style={{ minWidth: '120px' }}
                            body={r => (r.Fajl
                                ? <Button label={t('firmaAlarm.otvori')} icon="pi pi-paperclip" type="button"
                                    className="p-button-text p-button-sm"
                                    onClick={() => window.open(linkDokumenta(r.Fajl), '_blank')} />
                                : '-')} />
                    </DataTable>
                </Card>
            </div>
        </div>
    );
};

export default FirmaAlarmi;
